import translationEn from './locals/en/translation.json';
import translationFr from './locals/fr/translation.json';

/**
 * The one method of an i18next instance this module needs, described structurally rather than
 * imported as i18next's own `i18n` type. Hosts pin different i18next majors (client-dashboard 22,
 * partner-dashboard 25) and shared's own `import type` would resolve to whichever copy sits above
 * it in node_modules — so a nominal parameter type makes one host's instance a compile error at
 * this call even though it works perfectly at runtime.
 */
type SharedTranslationHost = {
  addResourceBundle(lng: string, ns: string, resources: object, deep?: boolean, overwrite?: boolean): unknown;
};

/**
 * Registers the shared bundle into the host's i18next instance. Call once, next to the host's
 * `i18n.init(...)`.
 *
 * Shared's keys go into the host's own `translations` namespace, under a reserved top-level
 * `Shared` key — NOT a namespace of their own. White-label clients reword copy by way of an
 * override file that is a subset of the `translations` tree, deep-merged over it
 * (`client-dashboard/src/app/hooks/useTranslationsOverride.ts`), and that merge only ever targets
 * `translations`. A separate namespace would put every shared string permanently out of a client's
 * reach. `.claude/skills/i18n-conventions/white-label-overrides.md` has the whole picture.
 *
 * BOTH the `translations` and `originalTranslations` bundles are written, and that is load-bearing:
 * `revertTranslations` in that hook drops the whole `translations` bundle and rebuilds it from
 * `originalTranslations` before applying a fresh override. Write only `translations` and the first
 * revert — any language switch — silently wipes every shared string, leaving each `t()` call to
 * fall back to its English `defaultValue`. Nothing catches that: no type, no guard, no spec.
 *
 * Each language gets its OWN deep copy on purpose. i18next merges into a resource bundle *in
 * place* (backend loads, later `addResourceBundle` calls, missing-key writes), so handing `fr` and
 * `fr-CA` one shared object reference would make a write under one silently appear under the
 * other. `packages/partner-dashboard/src/app/i18n/i18n.ts:32-35` documents the same hazard.
 * `en` is cloned too, so no caller can mutate this module's imported JSON.
 */
export function registerSharedTranslations(i18n: SharedTranslationHost) {
  const bundles = {
    en: translationEn,
    fr: translationFr,
    'fr-CA': translationFr,
  };

  Object.entries(bundles).forEach(([language, bundle]) => {
    ['translations', 'originalTranslations'].forEach((namespace) => {
      i18n.addResourceBundle(language, namespace, JSON.parse(JSON.stringify(bundle)), true, true);
    });
  });
}
